import {Action} from "redux";
import {ConfigState, TicketMap} from "../reducers/config";
import {ApplicationState} from "../reducers";
import {ThunkAction} from "redux-thunk";
import {ensureToken} from "./token";
import {apiBase} from "./api";

export const CONFIG_SET = "CONFIG_SET";
export const TICKETS_SET = "TICKETS_SET";
export const TICKETS_LOADING = "TICKETS_LOADING";

type ActionResponse = ThunkAction<Promise<ConfigAction>, ApplicationState, {}>;

export interface ConfigAction extends Action {
    config?: ConfigState;
    tickets?: TicketMap;
    loading?: boolean;
}

export interface CommonAction extends Action {
    errorMessage?: string;
}

export function apiError(type: string, err: any): CommonAction {
    const msg = err ? (
        err.message ? err.message : err
    ) : "Unknown error";
    return {
        type,
        errorMessage: msg
    };
}

/**
 * Make an API request - automatically includes the Token authorization header
 * @param {string} url - API url without the version (/auth/token not /api/v1/auth/token)
 * @param {RequestInit} options - fetch options
 * @returns {Promise<T>}
 */
export function apiRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
    if (!options) {
        options = {};
    }
    if (!options.headers) {
        options.headers = {};
    }
    return ensureToken().then(token => {
        (options.headers as any).Token = token.token;
        return fetch(apiBase() + url, options);
    }).then(response => {
        return response.text().then(txt => {
            try {
                return JSON.parse(txt);
            } catch (ex) {
                return Promise.reject(new Error("API error: " + txt));
            }
        });
    });
}

/**
 * Send a POST API call to url with body - forwards to apiRequest
 * @param {string} url
 * @param body
 * @returns {Promise<T>}
 */
export function apiPost<T>(url: string, body: any): Promise<T> {
    return apiRequest(url, {
        body: JSON.stringify(body),
        headers: {"Content-Type": "application/json"},
        method: "post",
    });
}

/**
 * Send a POST API call to url with body - forwards to apiRequest
 * @param {string} url
 * @param body
 * @param contentType
 * @returns {Promise<T>}
 */
export function apiPut<T>(url: string, body: any, contentType: string = "application/json"): Promise<T> {
    return apiRequest(url, {
        body: contentType === "application/json" ? JSON.stringify(body) : body,
        headers: {"Content-Type": contentType},
        method: "put",
    });
}

/**
 * Send a DELETE API call to url
 * @param {string} url
 * @param body
 * @param contentType
 * @returns {Promise<T>}
 */
export function apiDelete<T>(url: string, body?: any, contentType: string = "application/json"): Promise<T> {
    const options = {
        method: "delete",
    } as any;
    if (body) {
        options.body = contentType === "application/json" ? JSON.stringify(body) : body;
    }
    if (contentType) {
        options.headers = {"Content-Type": contentType};
    }
    return apiRequest(url, options);
}

export function loadConfig(): ActionResponse {
    return dispatch => {
        dispatch({type: TICKETS_LOADING, loading: true});
        return apiRequest<ConfigState>(`/config`).then(config => {
            return dispatch({
                type: CONFIG_SET,
                config
            });
        }) as Promise<ConfigAction>;
    };
}

export function loadTickets(): ActionResponse {
    return dispatch => {
        dispatch({type: TICKETS_LOADING, loading: true});
        return apiRequest<TicketMap>(`/tickets`).then(tickets => {
            return dispatch({
                type: TICKETS_SET,
                tickets
            });
        });
    };
}



// WEBPACK FOOTER //
// ./src/actions/common.ts